import api from './api';

/**
 * Sohoz Job Extension — Customer API service.
 *
 * Talks to the backend `/api/sohoz-job/customers` endpoints (Customer table,
 * scoped server-side by the authenticated user). The shared `api` instance's
 * response interceptor already unwraps to the response body, so each call
 * resolves to `{ success, message, data, pagination? }`.
 */
class SohozJobCustomerService {
    /** List customers for the current user, optionally filtered by a search query. */
    async list({ search = '' } = {}) {
        const body = await api.get('/api/sohoz-job/customers', {
            params: { ...(search ? { search } : {}), limit: 500 },
        });
        return { data: body.data || [], pagination: body.pagination };
    }

    /** Get a single customer by id. */
    async get(id) {
        const body = await api.get(`/api/sohoz-job/customers/${id}`);
        return { data: body.data || null };
    }

    /** Create a customer for the current user. */
    async create(data) {
        const body = await api.post('/api/sohoz-job/customers', data);
        return { data: body.data };
    }

    /** Update an existing customer. */
    async update(id, data) {
        const body = await api.put(`/api/sohoz-job/customers/${id}`, data);
        return { data: body.data };
    }

    /** Delete a customer. */
    async remove(id) {
        const body = await api.delete(`/api/sohoz-job/customers/${id}`);
        return { data: body?.data };
    }

    /** Extract form fields from pasted CV/resume text via AI. */
    async parseCv(text) {
        const body = await api.post('/api/sohoz-job/parse-cv', { text });
        return body.data || {};
    }

    /** Scan uploaded documents (PDF/image/docx) into form fields (not stored). */
    async parseCvFiles(files) {
        const fd = new FormData();
        for (const f of files) fd.append('files', f);
        const body = await api.post('/api/sohoz-job/parse-cv-files', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return body.data || {};
    }

    /** Upload a photo/signature image to R2; returns { url, key }. */
    async uploadImage(file) {
        const fd = new FormData();
        fd.append('file', file);
        const body = await api.post('/api/sohoz-job/upload-image', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return body.data; // { url, key }
    }

    // ── Messages the extension saved on a customer (steps + inputs; e.g. Teletalk payment SMS) ──
    /** { customer, messages } for one customer, newest first. */
    async listMessages(customerId) {
        const body = await api.get(`/api/sohoz-job/customers/${customerId}/messages`);
        return body.data || { customer: null, messages: [] };
    }

    /** Set (or clear, with '') a message input typed later — e.g. the PIN the 1st SMS's reply brought. */
    async setMessageInput(customerId, messageId, key, value) {
        const body = await api.put(`/api/sohoz-job/customers/${customerId}/messages/${messageId}/inputs/${encodeURIComponent(key)}`, { value });
        return body.data;
    }

    async removeMessage(customerId, messageId) {
        const body = await api.delete(`/api/sohoz-job/customers/${customerId}/messages/${messageId}`);
        return body?.data;
    }

    // ── Browser-extension keys ──
    async listKeys() {
        const body = await api.get('/api/sohoz-job/extension-tokens');
        return body.data || [];
    }

    async createKey(label) {
        const body = await api.post('/api/sohoz-job/extension-tokens', { label });
        return body.data; // includes raw token (shown once)
    }

    async revokeKey(id) {
        const body = await api.delete(`/api/sohoz-job/extension-tokens/${id}`);
        return body?.data;
    }
}

export default new SohozJobCustomerService();
